import { UserProfile } from 'src/types/common.types';
import { useCallback } from 'react';
import { usePersistStore } from '../common';
import { getUserId, isAuthenticated, refreshToken, logout as authLogout } from 'src/api';

export interface AuthOperations {
  login: (token: string, profile: UserProfile) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokenIfNeeded: () => Promise<boolean>;
  checkAuthenticationStatus: () => Promise<{
    isAuthenticated: boolean;
    profile: UserProfile | null;
  }>;
}

/**
 * Hook for authentication management operations
 * Handles login, logout, token refresh, and authentication checking
 * Separated from context to follow single responsibility principle
 */
export const useAuthManagement = (): AuthOperations => {
  const { getLocal, getSecure, putLocal, putSecure, clearAllNonPersistent } = usePersistStore();

  /**
   * Check and refresh tokens if needed
   */
  const refreshTokenIfNeeded = useCallback(async (): Promise<boolean> => {
    try {
      return await isAuthenticated();
    } catch (error) {
      console.error('AuthManager: Error refreshing token:', error);
      throw error;
    }
  }, []);

  /**
   * Check authentication status and get profile
   */
  const checkAuthenticationStatus = useCallback(async (): Promise<{
    isAuthenticated: boolean;
    profile: UserProfile | null;
  }> => {
    try {
      // Check if user is authenticated using Auth module
      const authenticated = await isAuthenticated();
      
      if (authenticated) {
        // Get user profile from storage
        const userProfile = await getLocal<UserProfile>('userProfile');
        
        if (userProfile) {
          return {
            isAuthenticated: true,
            profile: userProfile
          };
        } else {
          // If no profile exists, try to get userId for minimal profile
          const userId = await getUserId();
          if (userId) {
            return {
              isAuthenticated: true,
              profile: { userId } as UserProfile
            };
          } else {
            // If no userId exists, force logout
            await authLogout();
            return {
              isAuthenticated: false,
              profile: null
            };
          }
        }
      } else {
        return {
          isAuthenticated: false,
          profile: null
        };
      }
    } catch (error) {
      console.error('AuthManager: Failed to check authentication status:', error);
      // Error occurred, fallback to logged out state
      await authLogout();
      return {
        isAuthenticated: false,
        profile: null
      };
    }
  }, [getLocal]);

  /**
   * Login using token and profile
   */
  const login = useCallback(async (token: string, profile: UserProfile): Promise<void> => {
    try {
      const existingTokens = await getSecure<Record<string, unknown>>('auth_tokens');

      if (!existingTokens) {
        await putSecure('auth_tokens', {
          idToken: token,
          userId: profile.userId,
          expiresAt: Date.now() + 3600 * 1000,
        });
      } else {
        await putSecure('auth_tokens', {
          ...existingTokens,
          idToken: token,
          userId: profile.userId,
        });
      }
      
      // Store profile using storage
      await putLocal('userProfile', profile);
    } catch (error) {
      console.error('AuthManager: Login failed:', error);
      throw error;
    }
  }, [getSecure, putSecure, putLocal]);

  /**
   * Logout and clear all data
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Use the Auth module's logout function
      await authLogout();
      
      // Clear all non-persistent items
      await clearAllNonPersistent();
    } catch (error) {
      console.error('AuthManager: Logout failed:', error);
      throw error;
    }
  }, [clearAllNonPersistent]);

  return {
    login,
    logout,
    refreshTokenIfNeeded,
    checkAuthenticationStatus,
  };
};
